import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EventIcon from "@mui/icons-material/Event";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import TodayIcon from "@mui/icons-material/Today";
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  FormControl,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { UseHandleCustomers } from "0.Mine/1.Hooks/CRM/Customers";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { useSession } from "0.Mine/2.Context/sessionContext";
import { SafeArgonTypography, SafeArgonBox } from "0.Mine/4.Argon/ArgonComponents";
// Define types for reminder options
interface ReminderOption {
  id: string;
  label: string;
  value: string;
  icon?: React.ReactNode;
}

interface ReminderToggleProps {
  onChange: (selectedReminders: string[]) => void;
  defaultSelected?: string[];
  disabled?: boolean;
}

const ReminderToggle: React.FC<ReminderToggleProps> = ({
  onChange,
  defaultSelected = [],
  disabled = false,
}) => {
  const { t } = useTranslation();
  // Expanded reminder options
  /*   
    const reminderOptions: ReminderOption[] = [
        { id: '24h', label: t("reminderSelector.24HoursBefore"), value: '24_hours', icon: <AccessTimeIcon /> },
        { id: '48h', label: t("reminderSelector.48HoursBefore"), value: '48_hours', icon: <AccessTimeIcon /> },
        { id: '72h', label: t("reminderSelector.72HoursBefore"), value: '72_hours', icon: <AccessTimeIcon /> },
        { id: '1w', label: t("reminderSelector.1WeekBefore"), value: '1_week', icon: <CalendarTodayIcon /> },
        { id: '2w', label: t("reminderSelector.2WeeksBefore"), value: '2_weeks', icon: <CalendarTodayIcon /> },
        { id: '1month', label: t("reminderSelector.1MonthBefore"), value: '1_month', icon: <EventIcon /> },
        { id: 'registration', label: t("reminderSelector.onRegistration"), value: 'on_register', icon: <HowToRegIcon /> },
        { id: 'day-of', label: t("reminderSelector.DayOfEvent"), value: 'day_of', icon: <TodayIcon /> },
    ];
*/
  const reminderOptions: ReminderOption[] = [
    { id: "24h", label: t("24 ore prima"), value: "24_hours", icon: <AccessTimeIcon /> },
    { id: "48h", label: t("48 ore prima"), value: "48_hours", icon: <AccessTimeIcon /> },
    { id: "72h", label: t("72 ore prima"), value: "72_hours", icon: <AccessTimeIcon /> },
    { id: "1w", label: t("1 settimana prima"), value: "1_week", icon: <CalendarTodayIcon /> },
    { id: "2w", label: t("2 settimane prima"), value: "2_weeks", icon: <CalendarTodayIcon /> },
    { id: "1month", label: t("1 mese prima"), value: "1_month", icon: <EventIcon /> },
    {
      id: "registration",
      label: t("Alla registrazione"),
      value: "on_register",
      icon: <HowToRegIcon />,
    },
    { id: "day-of", label: t("Il giorno"), value: "day_of", icon: <TodayIcon /> },
  ];

  // State to track selected reminders
  const [selectedReminders, setSelectedReminders] = useState<string[]>(defaultSelected);

  // Add this effect to update selected reminders when defaultSelected changes
  useEffect(() => {
    setSelectedReminders(defaultSelected);
  }, [defaultSelected]);

  // Handle toggle change
  const handleReminderChange = (event: React.MouseEvent<HTMLElement>, newReminders: string[]) => {
    setSelectedReminders(newReminders);
    onChange(newReminders);
  };

  return (
    <SafeArgonBox elevation={0} sx={{ p: 2, borderRadius: 2 }}>
      <FormControl fullWidth>
        <SafeArgonTypography variant="subtitle1" fontWeight="medium" gutterBottom>
          {t("Invia promemoria ai clienti")}
        </SafeArgonTypography>

        <SafeArgonBox sx={{ overflowX: "auto" }}>
          <ToggleButtonGroup
            value={selectedReminders}
            onChange={handleReminderChange}
            aria-label={t("reminderSelector.reminderOptions")}
            disabled={disabled}
            color="primary"
            sx={{
              display: "flex",
              flexWrap: "nowrap", // Keep buttons on a single row
              "& .MuiToggleButtonGroup-grouped": {
                border: 1,
                borderColor: "divider",
                m: 0.5,
              },
            }}
          >
            {reminderOptions.map((option) => (
              <ToggleButton
                key={option.id}
                value={option.value}
                aria-label={option.label}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  textTransform: "none",
                  whiteSpace: "nowrap", // Prevent text wrapping within button
                  minWidth: "max-content",
                }}
              >
                {option.icon}
                <SafeArgonTypography>{option.label}</SafeArgonTypography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </SafeArgonBox>
      </FormControl>
    </SafeArgonBox>
  );
};

const ReminderSelector: React.FC = () => {
  const { sendReminderForBusiness, getRemindersForBusiness } = UseHandleCustomers();
  const [lastSelected, setLastSelected] = useState<string[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [isPending, setIsPending] = useState(false);
  const { t } = useTranslation();
  const { session } = useBusinessContext();

  useEffect(() => {
    const fetchData = async () => {
      if (!session) return;
      if (!session.selectedBusiness) return;
      try {
        const reminders = await getRemindersForBusiness(session.selectedBusiness.id);
        setLastSelected(reminders);
      } catch (error) {}
    };
    fetchData();
  }, [session]);

  const handleReminderChange = useCallback(
    (selectedReminders: string[]) => {
      console.log("Selected reminders:", selectedReminders);
      setLastSelected(selectedReminders);
      setIsPending(true);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(async () => {
        try {
          if (session.selectedBusiness === null) return;
          await sendReminderForBusiness(session.selectedBusiness.id, selectedReminders);
          console.log("API call made with:", selectedReminders);
        } catch (error) {
          console.error("Failed to send reminders:", error);
        } finally {
          setIsPending(false);
        }
      }, 800);
    },
    [session]
  );

  React.useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  if (!session) {
    return <p>Please select a business to continue...</p>;
  }

  return (
    <Box sx={{ width: "100%", mx: "auto", my: 4 }}>
      <Typography variant="h5" gutterBottom>
        {t("Gestione dei promemoria")}
        {isPending && (
          <Typography
            component="span"
            sx={{
              fontSize: "0.75rem",
              color: "text.secondary",
              ml: 1,
              fontStyle: "italic",
            }}
          >
            (saving...)
          </Typography>
        )}
      </Typography>
      <ReminderToggle onChange={handleReminderChange} defaultSelected={lastSelected} />
    </Box>
  );
};

export default ReminderSelector;
